import { Controller, useFormContext, get } from "react-hook-form";
import { cn } from "@/utils/cn";
import { cva } from "class-variance-authority";
import {
    Field,
    Label,
    Listbox as HeadlessListbox,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
} from "@headlessui/react";
import { ChevronDownIcon, CheckIcon } from "@heroicons/react/24/outline";
import React from "react";

const fieldVariants = cva("flex items-start", {
    variants: {
        labelPlacement: {
            start: "flex-row",
            end: "flex-row-reverse",
            top: "flex-col",
            bottom: "flex-col-reverse",
        },
        size: {
            default: "text-base",
            sm: "text-sm",
            lg: "text-lg",
        },
    },
    defaultVariants: {
        labelPlacement: "top",
        size: "default",
    },
});

const labelVariants = cva("mb-1 block w-full text-sm/6 font-medium", {
    variants: {
        variant: {
            primary: "text-darkgreen-500",
            success: "text-lime-700",
            danger: "text-red-500",
        },
    },
    compoundVariants: [
        {
            variant: ["primary", "success", "danger"],
            hideLabel: true,
            className: "sr-only w-fit",
        },
    ],
    defaultVariants: {
        variant: "primary",
        hideLabel: false,
    },
});

const buttonVariants = cva(
    "sm:text-md/6 block w-full min-w-0 appearance-none truncate rounded-3xl bg-white px-10 py-3 text-left text-base outline-2 -outline-offset-1 focus:outline-2 focus:-outline-offset-2 disabled:opacity-50 data-open:outline-2 data-open:-outline-offset-2",
    {
        variants: {
            variant: {
                primary:
                    "text-darkgreen-500 shadow-darkgreen-500/25 outline-neutral-200 placeholder:text-darkgreen-500 focus:outline-darkgreen-500/50 data-open:outline-darkgreen-500/50",
                success:
                    "text-lime-700 shadow-lime-500/25 outline-lime-500 placeholder:text-lime-700 focus:outline-lime-600",
                danger: "text-red-400 shadow-red-500/25 outline-red-500 placeholder:text-red-400 focus:outline-red-600",
            },
            size: {
                default: "font-base rounded-3xl py-3 pr-12 pl-5 text-base",
                sm: "font-sm rounded-3xl py-2 pr-12 pl-4 text-sm",
                lg: "font-lg rounded-3xl py-4 pr-12 pl-8 text-lg",
            },
            iconPosition: {
                left: "pl-12",
                right: "pr-12",
            },
        },
        defaultVariants: {
            variant: "primary",
            size: "default",

            iconPosition: "none",
        },
    },
);

const chevronVariants = cva(
    "group absolute top-1/2 right-5 size-4 -translate-y-1/2 stroke-[3]",
    {
        variants: {
            variant: {
                primary: "stroke-darkgreen-500/60 hover:stroke-darkgreen-500",
                success: "stroke-lime-700/60 hover:stroke-lime-700",
                danger: "stroke-red-500/60 hover:stroke-red-500",
            },
            size: {
                default: "",
                sm: "",
                lg: "",
            },
        },
        defaultVariants: {
            variant: "primary",
            size: "default",
        },
    },
);

const optionsVariants = cva(
    "z-100000 mb-3 w-(--button-width) overflow-y-auto rounded-xl outline-0 transition duration-100 ease-in empty:invisible data-leave:data-closed:opacity-0 [&::-webkit-scrollbar]:[width:6px] [&::-webkit-scrollbar-thumb]:[border-radius:3px] [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-track]:[margin:8px_0] [&>div]:mx-1.5 [&>div]:first:mt-1.5 [&>div]:last:mb-1.5",
    {
        variants: {
            variant: {
                primary: "bg-darkgreen-500",
                success: "bg-lime-500",
                danger: "bg-red-500",
            },
            size: {
                default:
                    "[--anchor-gap:--spacing(1)] [--anchor-padding:--spacing(1)]",
                sm: "[--anchor-gap:--spacing(1)] [--anchor-padding:--spacing(1)]",
                lg: "[--anchor-gap:--spacing(1)] [--anchor-padding:--spacing(1)]",
            },
        },
        defaultVariants: {
            variant: "primary",
            size: "default",
        },
    },
);

const optionVariants = cva(
    "group w-[calc(100% - 12px)] flex cursor-default items-center gap-2 rounded-lg py-1.5 select-none",
    {
        variants: {
            variant: {
                primary:
                    "text-white data-focus:bg-darkgreen-400 [&>svg]:stroke-white",
                success:
                    "text-black data-focus:bg-lime-600 [&>svg]:stroke-black",
                danger: "text-white data-focus:bg-red-600 [&>svg]:stroke-white",
            },
            size: {
                default: "px-3",
                sm: "px-2",
                lg: "px-3",
            },
        },
        defaultVariants: {
            variant: "primary",
            size: "default",
        },
    },
);

const iconVariants = cva(
    "pointer-events-none absolute top-1/2 shrink-0 -translate-y-1/2 stroke-2",
    {
        variants: {
            variant: {
                primary: "text-darkgreen-500",
                success: "text-lime-700",
                danger: "text-red-400",
            },
            size: {
                default: "size-5",
                sm: "size-4",
                lg: "size-6",
            },
            iconPosition: {
                left: "left-5",
                right: "right-5",
            },
        },
        defaultVariants: {
            variant: "primary",
            size: "default",
            iconPosition: "none",
        },
    },
);

export const Listbox = ({
    label,
    placeholder,
    name,
    className,
    disabled,
    multiple = false,
    hideLabel,
    variant,
    labelPlacement,
    size,
    children,
    icon: Icon,
    iconPosition,
}) => {
    const options = React.Children.toArray(children)
        .map((child, index) => {
            if (!React.isValidElement(child)) return null;

            if (React.isValidElement(child) && child.type === "option") {
                return {
                    type: "option",
                    value: child.props.value,
                    label: child.props.children,
                    disabled: child.props.disabled,
                };
            }

            if (child.type === ListboxHeader) {
                return {
                    type: "divider",
                    key: `divider-${index}`,
                    label: child.props.children,
                    disabled: child.props.disabled,
                };
            }
            return null;
        })
        .filter(Boolean);

    const {
        control,
        formState: { errors /*isSubmitted*/ },
    } = useFormContext();

    const hasError = get(errors, name);
    const formStateVariant = hasError
        ? "danger"
        : /*: isSubmitted
          ? "success"*/
          variant || "primary";

    const iconDefaultPosition = Icon ? (iconPosition ?? "left") : undefined;

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={multiple ? [] : ""}
            render={({ field }) => (
                <Field
                    disabled={disabled}
                    className={cn(fieldVariants({ labelPlacement, size }))}
                >
                    <Label
                        className={cn(
                            labelVariants({
                                variant: formStateVariant,
                                hideLabel,
                            }),
                        )}
                    >
                        {label}
                    </Label>
                    <div className="w-full">
                        <HeadlessListbox
                            value={
                                multiple
                                    ? Array.isArray(field.value)
                                        ? field.value
                                        : []
                                    : (field.value ?? "")
                            }
                            onChange={field.onChange}
                            multiple={multiple}
                        >
                            <div className="relative flex">
                                {Icon && (
                                    <Icon
                                        className={cn(
                                            iconVariants({
                                                variant: formStateVariant,
                                                size,
                                                iconPosition:
                                                    iconDefaultPosition,
                                            }),
                                        )}
                                    />
                                )}
                                <ListboxButton
                                    className={cn(
                                        buttonVariants({
                                            className,
                                            variant: formStateVariant,
                                            size,
                                            iconPosition: iconDefaultPosition,
                                        }),
                                    )}
                                >
                                    {multiple
                                        ? Array.isArray(field.value)
                                            ? options
                                                  .filter((o) =>
                                                      field.value.includes(
                                                          o.value,
                                                      ),
                                                  )
                                                  .map((o) => o.label)
                                                  .join(", ")
                                            : ""
                                        : options.find(
                                              (o) => o.value === field.value,
                                          )?.label ||
                                          placeholder ||
                                          ""}
                                    <ChevronDownIcon
                                        className={cn(
                                            chevronVariants({
                                                variant: formStateVariant,
                                                hideLabel,
                                            }),
                                        )}
                                    />
                                </ListboxButton>
                            </div>

                            <ListboxOptions
                                anchor="bottom start"
                                transition
                                className={cn(
                                    optionsVariants({
                                        variant: formStateVariant,
                                        size,
                                    }),
                                )}
                            >
                                {options.map((option) =>
                                    option.type === "divider" ? (
                                        <div
                                            key={option.key}
                                            className="mx-3! mt-4! mb-2! text-sm font-medium text-white"
                                        >
                                            {option.label}
                                        </div>
                                    ) : (
                                        <ListboxOption
                                            key={option.value}
                                            value={option.value}
                                            disabled={option.disabled}
                                            className={cn(
                                                optionVariants({
                                                    variant: formStateVariant,
                                                    size,
                                                }),
                                            )}
                                        >
                                            <CheckIcon className="invisible size-4 shrink-0 stroke-3 group-data-selected:visible" />
                                            <div className="text-sm/6">
                                                {option.label}
                                            </div>
                                        </ListboxOption>
                                    ),
                                )}
                            </ListboxOptions>
                        </HeadlessListbox>
                        {hasError && (
                            <div className="mt-2">
                                <InputError
                                    message={hasError.message}
                                    key={hasError.message}
                                />
                            </div>
                        )}
                    </div>
                </Field>
            )}
        />
    );
};

const InputError = ({ message }) => {
    return (
        <div className="px-4">
            <span role="alert" className="text-red-400">
                {message}
            </span>
        </div>
    );
};

export function ListboxHeader() {
    return null;
}

Listbox.Header = ListboxHeader;
